import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/** Dados passados ao abrir o diálogo via `MatDialog.open(VisualizadorImagemDialogComponent, { data })`. */
export interface VisualizadorImagemDialogData {
  /** Título exibido no cabeçalho. */
  titulo: string;
  /** `data:` URL pronto para `<img [src]>`. */
  dataUrl: string;
  /** Texto alternativo da imagem (default: `titulo`). */
  alt?: string;
}

/**
 * Diálogo Material para visualização de uma imagem com **zoom in/out**,
 * botão de **reset (100%)** e **scroll** automático quando a imagem
 * extrapolar o viewport (scrollbars nativas via `overflow: auto`).
 *
 * **Atalhos**
 *  - `Esc`           → fechar (nativo do `MatDialog`).
 *  - `Ctrl + Scroll` → zoom in/out (preserva o scroll comum da página).
 *
 * O `dataUrl` é confiável (vem do backend, com `mime` detectado por
 * magic numbers), por isso não passa por `DomSanitizer`. Caso receba
 * um URL com origem desconhecida, sanitize antes de chamar este dialog.
 */
@Component({
  selector: 'app-visualizador-imagem-dialog',
  templateUrl: './visualizador-imagem-dialog.component.html',
  styleUrls: ['./visualizador-imagem-dialog.component.scss'],
})
export class VisualizadorImagemDialogComponent {
  /** Fator de zoom atual. `1` = 100%. */
  zoom = 1;

  /** Mínimo permitido (~25%). */
  readonly zoomMin = 0.25;
  /** Máximo permitido (~800%). */
  readonly zoomMax = 8;
  /** Passo multiplicativo (cada clique +25%). */
  readonly zoomStep = 1.25;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: VisualizadorImagemDialogData,
    private dialogRef: MatDialogRef<VisualizadorImagemDialogComponent>,
  ) {}

  zoomIn(): void {
    this.zoom = Math.min(this.zoom * this.zoomStep, this.zoomMax);
  }

  zoomOut(): void {
    this.zoom = Math.max(this.zoom / this.zoomStep, this.zoomMin);
  }

  resetZoom(): void {
    this.zoom = 1;
  }

  fechar(): void {
    this.dialogRef.close();
  }

  /**
   * Ctrl + scroll do mouse altera o zoom. Sem o Ctrl, o scroll comum
   * da página/viewport continua funcionando normalmente — útil quando
   * o usuário ampliou a imagem e precisa navegar nela.
   */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey) {
      return;
    }
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  /** Percentual do zoom atual para o rótulo da toolbar. */
  get zoomPercentual(): number {
    return Math.round(this.zoom * 100);
  }
}
